import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { useAiUsage } from "@shared/api/hooks/useAiUsage";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// F-O — global AI narration usage meter (admin + editor; D11/D13).
// Read-only: just the month's % bar + counts. No budget breakdown table
// and no self-serve top-up — grants are dev-team CLI only; when blocked,
// the banner tells the admin who to call.

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-07" → { title: "July 2026", range: "1 Jul – 31 Jul, HKT" }
function monthLabel(month: string): { title: string; range: string } {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return { title: month, range: "HKT" };
  const name = MONTH_NAMES[m - 1];
  const lastDay = new Date(y, m, 0).getDate();
  const short = name.slice(0, 3);
  return {
    title: `${name} ${y}`,
    range: `1 ${short} – ${lastDay} ${short}, HKT`,
  };
}

function barColor(pct: number, blocked: boolean): string {
  if (blocked || pct >= 95) return "bg-red-500";
  if (pct >= 80) return "bg-amber-500";
  return "bg-primary";
}

const AiUsageMeter = () => {
  const navigate = useNavigate();
  const { isAdmin, role } = useAuth();
  const allowed = isAdmin || role === "editor";
  const { data, isLoading } = useAiUsage();

  useEffect(() => {
    if (!allowed) {
      toast.error("Admin or editor access required");
      navigate("/staff/exhibitions");
    }
  }, [allowed, navigate]);

  if (!allowed) return null;

  const pct =
    data && data.budgetChars > 0
      ? Math.round((data.usedChars / data.budgetChars) * 100)
      : data
        ? 100
        : 0;
  const label = data ? monthLabel(data.month) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="ai-usage-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/exhibitions")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">AI Usage Meter</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {isLoading || !data ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : (
          <div className="p-6 bg-white border border-border rounded-lg space-y-4">
            <div>
              <h2 className="font-heading font-semibold text-lg">
                {label?.title}
              </h2>
              <p className="text-sm text-muted-foreground">({label?.range})</p>
            </div>

            <div
              className="h-4 w-full rounded-full bg-muted overflow-hidden"
              data-testid="ai-usage-bar"
            >
              <div
                className={`h-full rounded-full transition-all ${barColor(pct, data.blocked)}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>

            <div className="flex items-baseline justify-between">
              <span className="text-sm" data-testid="ai-usage-count">
                {data.usedChars.toLocaleString()} /{" "}
                {data.budgetChars.toLocaleString()} characters
              </span>
              <span
                className="text-sm font-medium"
                data-testid="ai-usage-percent"
              >
                {pct}% used
              </span>
            </div>

            {data.blocked && (
              <div
                className="text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2"
                data-testid="ai-usage-blocked-banner"
              >
                ⛔ Monthly AI narration budget is used up. AI generation is
                blocked until the development team adds a top-up grant.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiUsageMeter;
